/**
 * The parts of the print screens that are logic rather than markup.
 *
 * Everything here mirrors a decision the API already enforces — which
 * statuses each transition accepts, which ones the printer CSV will export,
 * what a price may be. The point is not to re-implement the rules but to
 * let the screen *disable the control and say why* instead of offering an
 * action that comes back a 409 the operator has to decode.
 *
 * When the API's sets change, these change. They are named after the
 * server-side constants so a grep finds both:
 * `BULK_ELIGIBLE_FROM_STATUSES`, `CSV_ELIGIBLE_STATUSES`,
 * `CANCEL_FROM_STATUSES`, `MARK_PAID_FROM_STATUSES`
 * (aamcirc/admin/routers/print_admin.py).
 */

export interface PrintSetting {
  publication_slug: string
  enabled: boolean
  price_cents: number
  intl_enabled: boolean
  intl_price_cents: number | null
  interest_count: number
  /** Of `interest_count`, how many are not members — the ones a launch
   * email would reach for the first time. */
  interest_email_only: number
  /** Interested / Not interested on the public print page. No email is
   * needed to answer, so these reach readers the notify-me count misses. */
  sentiment_interested: number
  sentiment_not_interested: number
  /** While off, ask a signed-out visitor who says Interested for an
   * optional email (2026-10-16). Members are never asked. */
  ask_visitor_email: boolean
}

/** One row of the Interested readers list (2026-10-16). */
export interface InterestedPerson {
  kind: 'member' | 'visitor'
  subscriber_id: number | null
  name: string | null
  email: string | null
  account: string | null
  membership_status: string | null
  membership_ends: string | null
  since: string
  sources: Array<'said_interested' | 'notify_me'>
}

export interface InterestedList {
  publication_slug: string
  members: number
  visitors: number
  /** Anonymous Interested answers with no address: a count, never a name. */
  anonymous_interested: number
  items: InterestedPerson[]
}

const SOURCE_LABELS: Record<string, string> = {
  said_interested: 'Said Interested',
  notify_me: 'Left email',
}

/** "Said Interested + Left email". */
export function sourcesLabel(sources: string[]): string {
  return sources.map((s) => SOURCE_LABELS[s] ?? s).join(' + ')
}

/** "Active to Jan 1, 2027", "Expired Mar 3, 2026", or "No membership". */
export function membershipLabel(p: Pick<InterestedPerson, 'membership_status' | 'membership_ends'>): string {
  if (!p.membership_status) return 'No membership'
  if (!p.membership_ends) return p.membership_status
  const d = new Date(`${p.membership_ends}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  })
  return p.membership_status === 'Active' ? `Active to ${d}` : `${p.membership_status} ${d}`
}

/** "12 members · 3 visitor emails · 6 anonymous (no email)". */
export function interestedSummary(l: Pick<InterestedList, 'members' | 'visitors' | 'anonymous_interested'>): string {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
  return [
    plural(l.members, 'member', 'members'),
    plural(l.visitors, 'visitor email', 'visitor emails'),
    `${l.anonymous_interested} anonymous (no email)`,
  ].join(' · ')
}

/** The Interested / Not interested line under a title on the Print page,
 * or null until someone has answered. */
export function sentimentLine(s: PrintSetting): string | null {
  const yes = s.sentiment_interested
  const no = s.sentiment_not_interested
  if (yes + no === 0) return null
  const pct = Math.round((100 * yes) / (yes + no))
  return `Would buy in print: ${yes} interested · ${no} not interested (${pct}% interested)`
}

export interface PrintOrder {
  id: number
  status: string
  kind: string
  publication_slug: string
  edition: string
  subscriber_id: number
  /** "member" if the buyer holds any subscription for this order's
   * publication, "guest" otherwise. Not derived from anything else on this
   * order — a member's own order can carry no subscription_id at all — so
   * the queue reads it straight off the field the API computed. */
  buyer_kind: string
  is_gift: boolean
  gift_message: string | null
  price_cents: number
  ship_name: string
  ship_line1: string
  ship_line2: string | null
  ship_city: string
  ship_region: string | null
  ship_postal: string
  ship_country: string
  charge_attempts: number
  last_error: string | null
  created_at: string
  charged_at: string | null
  shipped_at: string | null
}

export interface PrintOrderList {
  items: PrintOrder[]
  total: number
}

// ---------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------

/**
 * A typed dollar amount as whole cents, or null if it is not a positive
 * amount of money.
 *
 * The API requires `price_cents > 0`, so a bad value has to be caught here
 * rather than arriving back as a 422. Rounds to the nearest cent: 19.999
 * was a typo for 20.00, not for 19.99, and the panel shows the rounded
 * value back so nothing is changed behind the operator's back.
 */
export function dollarsToCents(raw: string): number | null {
  const cleaned = raw.trim().replace(/[$,\s]/g, '')
  if (cleaned === '') return null
  if (!/^\d*\.?\d+$/.test(cleaned)) return null
  const cents = Math.round(Number(cleaned) * 100)
  if (!Number.isFinite(cents) || cents <= 0) return null
  return cents
}

/**
 * Cents as an editable dollar string — no currency symbol, because this
 * goes into a text input rather than onto the page.
 *
 * An unset price renders empty, never "0.00": a zero in the box reads as
 * "printing is free", and saving it is a 422.
 */
export function centsToDollars(cents: number | null): string {
  if (cents === null) return ''
  return (cents / 100).toFixed(2)
}

// ---------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------

/**
 * Why this settings row cannot be saved, or null if it can.
 *
 * The one that matters: `PUT /api/admin/settings/print` happily accepts
 * `intl_enabled: true` with no international price. `price_cents_for` then
 * raises `international_unpriced` at every overseas purchase and at every
 * publish — so the switch reads as on while every overseas member is turned
 * away, and nothing says so. Refuse the save.
 */
export function settingsProblem(s: PrintSetting): string | null {
  if (!(s.price_cents > 0)) {
    return 'Set a price per issue before turning print on.'
  }
  if (s.intl_enabled && (s.intl_price_cents === null || !(s.intl_price_cents > 0))) {
    return 'International printing is on with no international price, so every '
      + 'overseas order would be refused. Set the price, or turn international off.'
  }
  return null
}

// ---------------------------------------------------------------------
// Which controls a status permits
// ---------------------------------------------------------------------

export interface Permission {
  allowed: boolean
  /** Why not. Always set when `allowed` is false. */
  reason: string
}

export interface OrderActions {
  /** Bulk mark queued / shipped. */
  bulk: Permission
  /** Can appear in the printer CSV. */
  csv: Permission
  cancel: Permission
  markPaid: Permission
  refund: Permission
}

const BULK_ELIGIBLE_FROM = new Set(['charged', 'queued'])
const CSV_ELIGIBLE = new Set(['charged', 'queued', 'shipped'])
const CANCEL_FROM = new Set(['pending', 'in_review', 'failed', 'charged'])
const MARK_PAID_FROM = new Set(['pending', 'in_review', 'failed'])

const ok: Permission = { allowed: true, reason: '' }
const no = (reason: string): Permission => ({ allowed: false, reason })

/** Why a status is not eligible for the fulfilment actions, in words. */
function fulfilmentRefusal(status: string): string {
  switch (status) {
    case 'pending':
      return 'The charge has not gone through yet.'
    case 'in_review':
      return 'The gateway has not confirmed this charge either way.'
    case 'failed':
      return 'This charge did not go through.'
    case 'shipped':
      return 'Already shipped.'
    case 'cancelled':
      return 'This order was cancelled.'
    case 'refunded':
      return 'This order was refunded.'
    default:
      // Deliberately does NOT interpolate the status. This string reaches
      // the operator, including as a skipped-row reason straight off the
      // API, and a machine token in that sentence is not an explanation.
      return 'Not available for this order yet.'
  }
}

export function orderActions(status: string): OrderActions {
  return {
    bulk: BULK_ELIGIBLE_FROM.has(status) ? ok : no(fulfilmentRefusal(status)),
    csv: CSV_ELIGIBLE.has(status) ? ok : no(fulfilmentRefusal(status)),
    cancel: CANCEL_FROM.has(status)
      ? ok
      : no(
        status === 'queued'
          ? 'This order has already gone to the printer, so it cannot be '
            + 'stopped here.'
          : fulfilmentRefusal(status),
      ),
    markPaid: MARK_PAID_FROM.has(status)
      ? ok
      : no(
        status === 'charged'
          ? 'Already paid.'
          : fulfilmentRefusal(status),
      ),
    // There is no refund endpoint for a print order, by design rather than
    // omission: Refund.order_id is a NOT NULL foreign key to orders.id and
    // a PrintOrder has no Order behind it. Money comes back by hand.
    refund: no(
      'Print refunds are not wired up. Refund the charge in the '
      + 'Authorize.Net Merchant Interface, then cancel this order here if it '
      + 'has not shipped.',
    ),
  }
}

// ---------------------------------------------------------------------
// The bulk endpoint's answer
// ---------------------------------------------------------------------

export interface BulkResult {
  updated: number
  skipped: { id: number; reason: string }[]
}

export interface BulkSummary {
  tone: 'ok' | 'warn' | 'bad'
  message: string
  /** One line per skipped row, already in words. */
  skipped: string[]
}

/**
 * `{updated, skipped}` in words.
 *
 * The endpoint returns the skipped rows individually for a reason: a count
 * alone hides exactly which orders did not move, and those are the ones the
 * operator still has to deal with. So each one gets its own line.
 */
export function bulkSummary(result: BulkResult, target: string): BulkSummary {
  const n = result.updated
  const moved = `${n} ${n === 1 ? 'order' : 'orders'} marked ${target}.`
  const skipped = result.skipped.map(
    (s) => `#${s.id} — ${fulfilmentRefusal(s.reason).replace(/\.$/, '')}`,
  )
  if (result.skipped.length === 0) return { tone: 'ok', message: moved, skipped }
  if (n === 0) {
    return {
      tone: 'bad',
      message: `Nothing was marked ${target}.`,
      skipped,
    }
  }
  return { tone: 'warn', message: moved, skipped }
}

// ---------------------------------------------------------------------
// The rows that need a human
// ---------------------------------------------------------------------

/**
 * The `last_error` values that mean "nothing was charged, and no second
 * card will help, because the fault is ours" — our own Authorize.Net
 * credentials being rejected, or this deployment having none at all.
 *
 * Mirrors `aamcirc.printing.nonce_charge.OUR_FAULT_ERRORS` (itself
 * `AUTH_CODES` from `aamcirc.integrations.authnet_payments`, unioned with
 * `NOT_CONFIGURED`). Retyped here rather than imported because this bundle
 * ships to a browser and cannot reach into the Python process — but
 * `print.test.ts` reads both source files and fails the moment this list
 * stops matching them, so the two copies cannot drift apart silently.
 */
export const OUR_FAULT_ERRORS = new Set(['E00007', 'E00008', 'not_configured'])

/**
 * Why this order needs someone to look at it, or null.
 *
 * The queue is the only place these surface. Nothing alerts on them: a
 * stranded row lives in the worker log, and a failed charge sends no email
 * because print has no email at all yet.
 */
export function needsAttention(o: PrintOrder): string | null {
  if (o.status === 'in_review') {
    return 'The gateway has not said whether this charge went through. '
      + 'Check the Merchant Interface, then mark it paid or cancel it.'
  }
  if (o.status === 'failed') {
    if (o.last_error === 'no_payment_profile') {
      return 'There is no card on file to charge. The member needs to add one.'
    }
    if (o.last_error !== null && OUR_FAULT_ERRORS.has(o.last_error)) {
      // Deliberately worded to look nothing like the decline message below:
      // this is a count of OUR outage, not of declined or tested cards, and
      // it arrives in volume — every guest attempt fails while it lasts.
      return 'This did not fail because of the buyer\'s card. Our own '
        + 'Authorize.Net credentials failed or are not configured. A burst '
        + 'of these is an outage on our side, not a wave of declines — fix '
        + 'the credentials rather than treating it as fraud.'
    }
    return 'This charge did not go through.'
  }
  if (o.status === 'pending' && o.last_error === 'stranded_unresolved') {
    return 'Charged the gateway several times and it has no record of any of '
      + 'them. Money may or may not have moved — check the Merchant '
      + 'Interface before marking it paid.'
  }
  if (o.status === 'pending' && o.last_error === 'payment_unconfirmed') {
    return 'A guest charge that the gateway never answered. Nothing '
      + 'resolves this on its own: a one-time card charge is attached to no '
      + 'customer profile, so the automatic sweep cannot find it. Search '
      + 'for the order by its invoice number across the whole merchant '
      + 'account in the Authorize.Net Merchant Interface before marking it '
      + 'paid.'
  }
  return null
}

// ---------------------------------------------------------------------
// Paging
// ---------------------------------------------------------------------

export interface PageWindow {
  label: string
  hasPrev: boolean
  hasNext: boolean
}

export function pageWindow(
  // No `limit`: the window is fully determined by how many rows came back,
  // where they started, and how many match the filters. Taking the page size
  // as well would imply it matters here, and it does not.
  { total, offset, shown }: { total: number; offset: number; shown: number },
): PageWindow {
  if (total === 0) return { label: 'No orders', hasPrev: offset > 0, hasNext: false }
  // `shown` rather than `limit`: a filter can shrink the total under the
  // current offset, and claiming a range of rows that are not on screen is
  // worse than saying nothing.
  const label = shown === 0
    ? 'none on this page'
    : `${offset + 1}–${offset + shown} of ${total}`
  return {
    label,
    hasPrev: offset > 0,
    hasNext: offset + shown < total && shown > 0,
  }
}
